import { useState, useRef, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';
import { Modal, ModalTitle, ModalContent, ModalFooter, Button, Tooltip } from '@utahdts/utah-design-system';
import './WaterSystemSummaryTable.css';
import { formatDate } from '../../utilities/commonFunctions/formattingFunctions';
import { GET_WATER_SYSTEM_LAT_LONG, GET_WATER_SYSTEM_LF_URL_LINK } from '../../queries/publicQrys/SystemHeaderViewQrys';

const DATE_ONLY_OPTIONS = {
  hour: undefined,
  minute: undefined,
  second: undefined,
  hour12: undefined
};

const formatOptionalDecimal = (value) => (
  typeof value === 'number' ? value.toFixed(1) : null
);


const WaterSystemSummaryTable = ({
  selectedWaterSystem,
  waterSystemHomeDetailUrls = [],
  panelLock = false
}) => {
  const [expanded, setExpanded] = useState(Boolean(panelLock));
  const [isPanelLocked, setIsPanelLocked] = useState(Boolean(panelLock));
  const hoverTimerRef = useRef(null);
  const [showNoFolderModal, setShowNoFolderModal] = useState(false);

  const waterSystemHomeUrls = waterSystemHomeDetailUrls;

  const getMenuUrlRoute = (matchText) => {
    const match = waterSystemHomeUrls.find((item) =>
      item?.code === matchText
    );

    return match?.url_route || '';
  };

  const surveyNotesRoute = getMenuUrlRoute('SITE_VISIT_NOTES');
  const surveyNotesUrl = surveyNotesRoute
    .replace(/%systemId%/g, encodeURIComponent(selectedWaterSystem?.systemId || ''));

  const systemCallLogRoute = getMenuUrlRoute('SYSTEM_CALL_LOG');
  const systemCallLogUrl = systemCallLogRoute
    .replace(/%systemId%/g, encodeURIComponent(selectedWaterSystem?.systemId || ''));

  const pwsRequirementsRoute = getMenuUrlRoute('PWS_REQUIREMENTS');
  const pwsRequirementsUrl = pwsRequirementsRoute
    .replace(/%systemId%/g, encodeURIComponent(selectedWaterSystem?.systemId || ''));

  const mapsRoute = getMenuUrlRoute('MAPS');
  const eqDocsRoute = getMenuUrlRoute('EQDOCS');

  const { data: latLongData } = useQuery(GET_WATER_SYSTEM_LAT_LONG, {
    variables: { pwsId: selectedWaterSystem?.pwsId },
    skip: !mapsRoute || !selectedWaterSystem?.pwsId
  });

  const { data: eqDocsData } = useQuery(GET_WATER_SYSTEM_LF_URL_LINK, {
    variables: { pwsId: selectedWaterSystem?.pwsId },
    skip: !eqDocsRoute || !selectedWaterSystem?.pwsId
  });

  const mapsUrl = useMemo(() => {
    const coords = latLongData?.getSystemLatLongCoords;
    const hasLatitude = coords?.latitude !== null && coords?.latitude !== undefined;
    const hasLongitude = coords?.longitude !== null && coords?.longitude !== undefined;

    if (!hasLatitude || !hasLongitude) {
      return '';
    }

    return `https://maps.google.com?q=${coords.latitude},${coords.longitude}`;
  }, [latLongData]);

  const eqDocsUrl = useMemo(() => (
    eqDocsData?.getLFLinkbySystemId?.urlLink || ''
  ), [eqDocsData]);

  const handleEqDocsClick = (event) => {
    if (!eqDocsUrl) {
      event.preventDefault();
      setShowNoFolderModal(true);
    }
  };

  useEffect(() => {
    if (panelLock) {
      setIsPanelLocked(true);
      setExpanded(true);
    }
  }, [panelLock]);

  const system = {
    ...(selectedWaterSystem ?? {}),
    adminContact: selectedWaterSystem?.adminContact ?? {},
    legalContact: selectedWaterSystem?.legalContact ?? {},
    lastSurvey: selectedWaterSystem?.lastSurvey ?? {},
    operatingPeriod: selectedWaterSystem?.operatingPeriod ?? {},
    rating: selectedWaterSystem?.rating ?? {},
    waterUsage: selectedWaterSystem?.waterUsage ?? {}
  };

  const displayInfo = {
    activityStatus: system.activityStatus,
    countyServed: system.countyServed,
    ipsLastUpdateTs: formatDate(system.rating.ipsLastUpdateTs, DATE_ONLY_OPTIONS),
    lastInventoryUpdateDate: formatDate(system.lastInventoryUpdateDate, DATE_ONLY_OPTIONS),
    population: system.population,
    pwsId: system.pwsId,
    rating: system.rating.rating,
    ratingDate: formatDate(system.rating.ratingDate, DATE_ONLY_OPTIONS),
    requiredToDisinfectDate: system.requiredToDisinfectDate
      ? formatDate(system.requiredToDisinfectDate, DATE_ONLY_OPTIONS)
      : null,
    systemName: system.systemName,
    systemType: system.systemType,
    surveyDate: formatDate(system.lastSurvey.surveyDate, DATE_ONLY_OPTIONS),
    waterUsageAvgAnnualDemand: formatOptionalDecimal(system.waterUsage.avgAnnualDemand),
    waterUsageCreatedDate: formatDate(system.waterUsage.createdTs, DATE_ONLY_OPTIONS),
    waterUsageEqualizationStorage: formatOptionalDecimal(system.waterUsage.equalizationStorage),
    waterUsagePeakDayDemand: formatOptionalDecimal(system.waterUsage.peakDayDemand)
  };

  let ratingClass;
  switch (displayInfo.rating) {
    case 'Approved':
      ratingClass = 'rating-approved';
      break;
    case 'Corrective Action':
      ratingClass = 'rating-correction';
      break;
    case 'Not Approved':
      ratingClass = 'rating-not-approved';
      break;
    default:
      ratingClass = 'rating-none';
      break;
  }

  const showWaterUsage = displayInfo.waterUsageAvgAnnualDemand || displayInfo.waterUsagePeakDayDemand || displayInfo.waterUsageEqualizationStorage;

  const handleMouseEnter = () => {
    if (isPanelLocked) {
      return;
    }

    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setExpanded(true), 300);
  };

  const handleMouseLeave = () => {
    if (isPanelLocked) {
      return;
    }

    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setExpanded(false), 300);
  };

  const handleTogglePanelLock = () => {
    clearTimeout(hoverTimerRef.current);
    setIsPanelLocked((prev) => {
      const nextLocked = !prev;
      if (nextLocked) {
        setExpanded(true);
      }
      return nextLocked;
    });
  };
  const referenceElement = useRef(null);

  return (
    <>
    <div className="utah-design-system visible-for-print">
    <Tooltip className="ws-lock-tooltip" referenceElement={referenceElement.current}>
      {isPanelLocked ? 'Click to unlock panel' : 'Click to lock panel open'}
    </Tooltip>
    <div className="water-system-summary-table" onMouseLeave={handleMouseLeave}>
      <div className="water-system-summary-header" onMouseEnter={handleMouseEnter} >
        <div className="water-system-name">
          <div className="water-system-name-row">
            <div className="system-name">Water System: {displayInfo.systemName}</div>
            <div className="pws-id">PWS ID: {displayInfo.pwsId}</div>
          </div>
        </div>
        <div className={`water-system-rating ${ratingClass}`}>
          <div className="water-system-rating-row">
            <div className="rating">Rating: {displayInfo.rating}</div>
            <div>{displayInfo.ratingDate}</div>
            <div className="status">Status: {displayInfo.activityStatus}</div>
          </div>
        </div>
        <button
          ref={referenceElement}
          className={`collapse-icon-btn ${ratingClass}`}
          onClick={handleTogglePanelLock}
          aria-label={isPanelLocked ? 'Unlock panel' : 'Lock panel open'}
          aria-expanded={expanded}
          aria-controls="water-system-info-wrapper"
        >

          <FontAwesomeIcon icon={isPanelLocked ? faLock : faLockOpen} fixedWidth className="lock-icon" aria-hidden="true"/>
          <FontAwesomeIcon icon={isPanelLocked || expanded ? faChevronDown : faChevronRight} fixedWidth className="collapse-icon" aria-hidden="true"/>
        </button>
      </div>
      <div className="clearfix"></div>
      <div
        className={`water-system-info-wrapper${expanded ? ' expanded' : ''}`}
        id="water-system-info-wrapper"
      >
        <div className="water-system-info">
          <div className="col4 col4-light contacts">
            <h2 className="water-system-summary-subheading">Contact Details</h2>
            <div className="info-row"><strong>Type:</strong> Admin Contact</div>
            <div className="info-row"><strong>Name:</strong> {system.adminContact.adminFirstName} {system.adminContact.adminLastName}</div>
            <div className="info-row"><strong>Office:</strong> {system.adminContact.adminPhone}</div>
            <div className="info-row"><strong>Emergency:</strong> {system.adminContact.adminEmergencyPhone}</div>
            <div className="info-row">
              <strong>Email:</strong>
              <div>{system.adminContact.email}</div>
              <div>{system.adminContact.email2}</div>
              <div>{system.adminContact.email3}</div>
            </div>
          </div>
          <div className="col4 col4-dark site-info">
            <h2 className="water-system-summary-subheading">Site Information</h2>
            {mapsRoute && mapsUrl && (
              <div className="info-row"><a href={mapsUrl} target="_blank" rel="noopener noreferrer">View on Map</a></div>
            )}
            <div className="info-row"><strong>Legal Contact:</strong> {system.legalContact.lcFirstName}</div>
            <div className="info-row">
              <strong>Address:</strong> {system.legalContact.lcAddress1} {system.legalContact.lcAddress2}<br />
              {system.legalContact.lcCity}, {system.legalContact.lcState} {system.legalContact.lcZip}
            </div>
            <div className="info-row"><strong>Phone:</strong> {system.legalContact.phone}</div>
            <div className="info-row"><strong>County:</strong> {displayInfo.countyServed}</div>
            <div className="info-row"><strong>System Type:</strong> {displayInfo.systemType}</div>
            <div className="info-row"><strong>Certification Required:</strong> {system.legalContact.certType}</div>
            <div className="info-row"><strong>Total Population:</strong> {displayInfo.population}</div>
            <div className="info-row"><strong>Local Health District:</strong> {system.legalContact.lhdName}</div>
          </div>
          <div className="col4 col4-light site-updates">
            <h2 className="water-system-summary-subheading">Site Updates</h2>
            {surveyNotesRoute && (
              <div className="info-row">
                <a href={surveyNotesUrl} target="_blank" rel="noopener noreferrer">View Survey Notes</a>
              </div>
            )}
            <div className="info-row"><strong>Last Inventory Update:</strong> {displayInfo.lastInventoryUpdateDate}</div>
            <div className="info-row"><strong>Last Surveyor Update:</strong> {displayInfo.surveyDate}</div>
            <div className="info-row"><strong>Surveyor:</strong> {system.lastSurvey.surveyorFirstName} {system.lastSurvey.surveyorLastName}</div>
            <div className="info-row"><strong>Operating Period:</strong> {system.operatingPeriod.operatorStartMonth}/{system.operatingPeriod.operatorStartDay} - {system.operatingPeriod.operatorEndMonth}/{system.operatingPeriod.operatorEndDay}</div>
            <div className="info-row"><strong>Last IPS Update:</strong> {displayInfo.ipsLastUpdateTs}</div>
            {displayInfo.requiredToDisinfectDate && (
              <div className="info-row" style={{ marginTop: '2rem' }}><strong>System is required to disinfect as of: {displayInfo.requiredToDisinfectDate}</strong></div>
            )}
          </div>
          <div className="col4 col4-dark political-districts">
            <h2 className="water-system-summary-subheading">Political Districts</h2>
            <div className="info-row"><a href="https://le.utah.gov/GIS/findDistrict.jsp">Legislative District Map</a></div>
            <div className="info-row" style={{ marginTop: '0.5rem' }}><strong>Water Usage Information per ERC</strong></div>
            {showWaterUsage && (
              <>
                <div className="info-row">Standard as of: {displayInfo.waterUsageCreatedDate}</div>
                <div className="info-row">Peak Day Demand per ERC (gal/ERC): {displayInfo.waterUsagePeakDayDemand}</div>
                <div className="info-row">Average Annual Demand per ERC (gal/ERC): {displayInfo.waterUsageAvgAnnualDemand}</div>
                <div className="info-row">Equalization Storage per ERC (gal/ERC): {displayInfo.waterUsageEqualizationStorage}</div>
              </>
            )}
            <div className="info-row"><strong>Total IPS Points: </strong>{system.rating.totalIpsPoints}</div>
            {eqDocsRoute && (
              <div className="info-row">
                <a
                  className="hyperlink"
                  href={eqDocsUrl || '#'}
                  target={eqDocsUrl ? '_blank' : undefined}
                  rel={eqDocsUrl ? 'noopener noreferrer' : undefined}
                  onClick={handleEqDocsClick}
                >
                  View EQDocs
                </a>
              </div>
            )}
            {systemCallLogRoute && (
              <div className="info-row"><a href={systemCallLogUrl} target="_blank" rel="noopener noreferrer">View System Call Log</a></div>
            )}
            {pwsRequirementsRoute && (
              <div className="info-row"><a href={pwsRequirementsUrl} target="_blank" rel="noopener noreferrer">View PWS Requirements</a></div>
            )}
          </div>
        </div>
      </div>
    </div>
    </div>
    {showNoFolderModal && (
      <div className="utah-design-system">
        <Modal className="modal--medium" onClose={() => setShowNoFolderModal(false)}>
          <ModalTitle>No Folder Found</ModalTitle>
          <ModalContent>There is no folder yet created in Laserfiche. Please contact Rich Peterson or Hunter Payne.</ModalContent>
          <ModalFooter>
            <Button onClick={() => setShowNoFolderModal(false)}>OK</Button>
          </ModalFooter>
        </Modal>
      </div>
    )}
    </>
  );
}
export default WaterSystemSummaryTable;
